"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = require("./util");
var DojoMetrics = /** @class */ (function () {
    function DojoMetrics(config) {
        var _this = this;
        this.logSocketErrors = (0, util_1.throttle)(function (err) {
            var plog = _this.getLogFunctions().plog;
            plog("metricError_closedSocket: failed to send metric on closed socket \u2013 error:\u00A0".concat(err));
        }, 5000);
        /**
         * Binding so we can pass `metrics.increment` and so on without having to bind them.
         */
        this.increment = function (metricName, maybeValue, inputTags) {
            var value;
            var tags = inputTags;
            if (typeof maybeValue === "number") {
                value = maybeValue;
            }
            else {
                tags = maybeValue;
                value = 1;
            }
            return _this.doOp("increment", metricName, value, tags);
        };
        this.gauge = function (metricName, value, tags) {
            return _this.doOp("gauge", metricName, value, tags);
        };
        this.timing = function (metricName, value, tags) {
            return _this.doOp("timing", metricName, value, tags);
        };
        this.histogram = function (metricName, value, tags) {
            return _this.doOp("histogram", metricName, value, tags);
        };
        this.distribution = function (metricName, value, tags) {
            return _this.doOp("distribution", metricName, value, tags);
        };
        this.makeTimer = function (metricName, maybeMaxDuration, inputTags) {
            var tags = inputTags;
            if (typeof maybeMaxDuration !== "number") {
                tags = maybeMaxDuration;
                maybeMaxDuration = undefined;
            }
            var maxDuration = maybeMaxDuration || _this.config.maxTimerDuration || 10000;
            var instance = _this;
            var startTime = Date.now();
            var timeout;
            var timer = {
                stop: function () {
                    if (timer.stopped || timer.canceled || timer.timedOut)
                        return;
                    var duration = Date.now() - startTime;
                    instance.timing(metricName + ".duration", duration, tags);
                    timer.duration = duration;
                    timer.stopped = true;
                    if (timeout)
                        clearTimeout(timeout);
                },
                timeOut: function () {
                    if (timer.timedOut || timer.stopped || timer.canceled)
                        return;
                    instance.timing(metricName + ".duration", maxDuration, tags);
                    instance.increment(metricName + ".timeout", tags);
                    timer.timedOut = true;
                    timer.duration = maxDuration;
                    if (timeout)
                        clearTimeout(timeout);
                },
                cancel: function () {
                    timer.canceled = true;
                    if (timeout)
                        clearTimeout(timeout);
                },
                unref: function () {
                    if (timeout)
                        timeout.unref();
                    return timer;
                },
                ref: function () {
                    if (timeout)
                        timeout.ref();
                    return timer;
                },
            };
            timeout = setTimeout(timer.timeOut, maxDuration);
            return timer;
        };
        this.close = function (maybeCb) {
            var cb = maybeCb || util_1.noop;
            var closedCount = 0;
            var errors = [];
            _this.backends.forEach(function (logger, i) {
                var name = _this.config.backends[i];
                logger.close(function (err) {
                    if (err) {
                        console.log("error on logger", name);
                        errors.push(err);
                    }
                    if (++closedCount === _this.backends.length) {
                        if (errors.length) {
                            var multiError = new Error("Something went wrong closing backends");
                            multiError.errors = errors;
                            return cb(multiError);
                        }
                        return cb();
                    }
                });
            });
        };
        // User must provide an app name
        if (!config.app)
            throw new Error("Must provide the name of your app");
        if (!config.env)
            throw new Error("Must provide the application env");
        // Default onError is simply logging to `console.error`, with the stack.
        config.onError =
            config.onError ||
                function (err) {
                    console.error("METRICS ERROR:", err, err.stack);
                };
        // User must provide at least one logger type.
        if (!config.backends || config.backends.length === 0) {
            throw new Error("Must select at least one backend");
        }
        this.config = config;
        this.backends = config.backends;
    }
    /**
     * To perform an operation, invoke the corresponding method on all backends.
     * Default tags are `app` and `env`, but can be extended.
     */
    DojoMetrics.prototype.doOp = function (operation, metricName, metricValue, metricTags) {
        var tags = __assign(__assign({ app: this.config.app, env: this.config.env }, this.config.defaultTags), metricTags);
        for (var _i = 0, _a = this.backends; _i < _a.length; _i++) {
            var backend = _a[_i];
            try {
                backend[operation](metricName, metricValue, tags);
            }
            catch (err) {
                var plog = this.getLogFunctions().plog;
                var errStack = err instanceof Error ? err.stack : "";
                plog("Unexpected metric error", err, errStack);
                if (this.isTesting())
                    throw err;
            }
        }
        return;
    };
    DojoMetrics.prototype.updateConfig = function (newConfig) {
        if (newConfig.backends)
            throw new Error("Cannot change backends once set.");
        newConfig.defaultTags = __assign(__assign({}, this.config.defaultTags), newConfig.defaultTags);
        this.config = __assign(__assign({}, this.config), newConfig);
    };
    DojoMetrics.prototype.isTesting = function () {
        return this.config.env === "testing" || this.config.env === "test";
    };
    DojoMetrics.prototype.getLogFunctions = function () {
        var instance = this;
        function makeLogger(toIncrement, prependToLog, appendToLog) {
            return function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (toIncrement)
                    instance.increment(toIncrement);
                if (prependToLog)
                    args.unshift(prependToLog);
                if (appendToLog)
                    args.push(appendToLog);
                console.log.apply(console, args);
            };
        }
        if (this.isTesting()) {
            return {
                plog: util_1.noop,
                flog: util_1.noop,
                clog: util_1.noop,
                flaw: function (str, err) {
                    if (!(0, util_1.isString)(str) || !(0, util_1.isError)(err)) {
                        throw new Error("Improper usage of console.flaw. Must provde a string and an error.");
                    }
                },
            };
        }
        else {
            return {
                plog: makeLogger(),
                flog: makeLogger("loggedErrors", "LOGGED ERROR:"),
                clog: makeLogger("loggedClientErrors", "clientError:"),
                flaw: function (str, err) {
                    if (!(0, util_1.isString)(str) || !(0, util_1.isError)(err)) {
                        throw new Error("Improper usage of console.flaw. Must provde a string and an error.");
                    }
                    makeLogger("loggedErrors", "LOGGED ERROR:", err.stack)(str, err);
                },
            };
        }
    };
    return DojoMetrics;
}());
exports.default = DojoMetrics;
//# sourceMappingURL=index.js.map